import { clsx } from 'clsx';
import type { ClassValue as ClassNameValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { v4 as uuId } from 'uuid';
import type { V4Options as UUIdOptions } from 'uuid';

import type { BaseTreeOption } from 'types';
import { FILE, PATHNAME } from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  QUERY_PARAM: {
    DEFAULT: { COMMA: DEFAULT_COMMA, PAGE_NUMBER: DEFAULT_PAGE_NUMBER },
  },
} = PATHNAME;
const {
  UNIT: {
    DEFAULT: { KILOBYTE: UNIT_DEFAULT_KILOBYTE, SIZES: UNIT_DEFAULT_SIZES },
    ...UNIT_LOCALE
  },
} = FILE;

const randomId = (options?: UUIdOptions) => uuId(options);

const cn = (...inputs: ClassNameValue[]) => twMerge(clsx(inputs));

const createTimestamp = (ms: number) => Date.now() + ms;

const normalizeToString = (input: any = ''): string => input.toString();

const combineNumber = (value: string, replaceValue: string = DEFAULT_COMMA) =>
  value.replace(/\B(?=(\d{3})+(?!\d))/gim, replaceValue);

const fixedParseFloat = (value: number, fractionDigits = 0) =>
  parseFloat(value.toFixed(fractionDigits));

const getObjectEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as Array<[keyof T, T[keyof T]]>;

const getObjectValues = <T extends object>(obj: T) =>
  Object.values(obj).filter(Boolean) as Array<NonNullable<T[keyof T]>>;

const deepFlatFilter = <T>(data: T[], depth = Infinity) =>
  data.flat(depth).filter(Boolean) as Array<NonNullable<T>>;

const validateMediaFile = (name: string, allowedFormats: readonly string[]) =>
  allowedFormats.some(format =>
    name.toLowerCase().endsWith(format.toLowerCase()),
  );

const joinEncodedSegments = (
  segments: string[],
  separator: string = DEFAULT_SLUG_SEPARATOR,
) => encodeURIComponent(segments.join(separator));

const joinDecodedSegments = (
  segments: string[],
  separator: string = DEFAULT_SLUG_SEPARATOR,
) => decodeURIComponent(segments.join(separator));

const filterObjectFromEntries = <T extends object, K extends Array<keyof T>>(
  obj: T,
  fields: K,
) =>
  Object.fromEntries(
    getObjectEntries(obj).filter(([key]) => !fields.includes(key)),
  ) as StrictOmit<T, K[number]>;

const getObjectFromEntries = <T extends object, K extends Array<keyof T>>(
  obj: T,
  fields: K,
) =>
  Object.fromEntries(
    getObjectEntries(obj).filter(([key]) => fields.includes(key)),
  ) as Pick<T, K[number]>;

const parseFromContent = (
  content?: string,
  type: DOMParserSupportedType = 'text/html',
) => {
  if (!content || typeof window === 'undefined') return;
  return new DOMParser().parseFromString(content, type).body;
};

const createRange = (start: number, end: number) =>
  Array.from(
    { length: end - start + DEFAULT_PAGE_NUMBER },
    (_, idx) => idx + start,
  );

const padString = (
  value: string,
  type: 'padStart' | 'padEnd' = 'padStart',
  maxLength = 2,
  fillString = '0',
) => value[type](maxLength, fillString);

const safeParseBoolean = (input: unknown): boolean | undefined => {
  const normalizedInput = normalizeToString(input).toLowerCase().trim();

  if (normalizedInput === 'true') return true;
  if (normalizedInput === 'false') return false;

  return undefined;
};

const replaceExtraSeparator = (
  value: string,
  type: 'clean' | 'revert' = 'clean',
  replaceValue = '-',
) =>
  value
    .trim()
    .replace(
      type === 'clean' ? /[^\p{L}\d_-]+/gu : /[\p{L}\d_-]+/gu,
      replaceValue,
    );

const preciseRound = (size: number, amount = 1, decimals = 3) => {
  const multiplier = Math.pow(10, Math.abs(decimals));
  return Math.round(size * amount * multiplier) / multiplier;
};

const hasIntersection = <T>(i1: Iterable<T>, i2: Iterable<T>) => {
  const secondIterable = new Set(i2);
  if (secondIterable.size === 0) return false;
  return Array.from(i1).some(value => secondIterable.has(value));
};

const adjustPageNumber = (
  total: number,
  size: number,
  page: number,
  updatePage: (page: number) => void,
) => {
  const totalPage = Math.ceil(total / size);
  if (page > totalPage && totalPage >= DEFAULT_PAGE_NUMBER) {
    updatePage(totalPage);
  }
};

const flatMapTreeOption = <T extends BaseTreeOption<T>>(options: T[]): T[] => {
  if (options.length === 0) return [];
  return options.flatMap(option => [
    option,
    ...(option.children ? flatMapTreeOption(option.children) : []),
  ]);
};

const throwErrorAsResponse = (
  error: unknown,
  fallbackMessage = 'خطای ناشناخته‌ای رخ داده است.',
  fallbackStatus = 500,
): never => {
  if (error instanceof Response) {
    throw error;
  }

  const body =
    error instanceof Error ? error.message : String(error ?? fallbackMessage);

  throw new Response(body, {
    status: fallbackStatus,
  });
};

const triggerPathname = (pathname: string, triggers: string[]) =>
  triggers.some(trigger => {
    const decodedPathname = joinDecodedSegments(
      pathname.split(DEFAULT_SLUG_SEPARATOR),
    );
    const decodedTrigger = joinDecodedSegments(
      trigger.split(DEFAULT_SLUG_SEPARATOR),
    );
    const splitedTrigger = trigger
      .split(DEFAULT_SLUG_SEPARATOR)
      .filter(Boolean);

    if (splitedTrigger.length > 0) {
      return (
        decodedPathname === decodedTrigger ||
        decodedPathname.startsWith(decodedTrigger)
      );
    }
    return decodedPathname === decodedTrigger;
  });

const getTimeProgress = (
  targetDate: Date,
  middleDate: Date,
  startDate: Date,
) => {
  const targetDateTime = targetDate.getTime();
  const middleDateTime = middleDate.getTime();
  const startDateTime = startDate.getTime();

  const isBeforeStart = middleDateTime < startDateTime;
  const isAfterTarget = middleDateTime > targetDateTime;
  const isOutOfRange = isBeforeStart || isAfterTarget;

  const timeRange = targetDateTime - startDateTime;
  const timeElapsed = !isOutOfRange ? middleDateTime - startDateTime : 0;
  const timeRemaining = !isOutOfRange ? targetDateTime - middleDateTime : 0;
  const timeProgress = isBeforeStart
    ? 0
    : isAfterTarget
      ? 1
      : fixedParseFloat(timeElapsed / timeRange, 2);

  return {
    targetDateTime,
    middleDateTime,
    startDateTime,
    isBeforeStart,
    isAfterTarget,
    isOutOfRange,
    timeRange,
    timeElapsed,
    timeRemaining,
    timeProgress,
  };
};

const getFileLocaleUnitIndex = (
  locale: keyof typeof UNIT_LOCALE,
  unit: (typeof UNIT_LOCALE)[typeof locale][(typeof UNIT_DEFAULT_SIZES)[number]],
) => {
  const [key] =
    getObjectEntries(UNIT_LOCALE[locale]).find(
      ([_, localizedUnit]) => localizedUnit === unit,
    ) ?? [];

  if (!key) return null;

  return UNIT_DEFAULT_SIZES.indexOf(key);
};

const createFileLocaleUnitSize = (
  value: number,
  locale: keyof typeof UNIT_LOCALE = 'EN',
  unit: (typeof UNIT_LOCALE)[typeof locale][(typeof UNIT_DEFAULT_SIZES)[number]] = 'MB',
) => {
  if (value <= 0) return 0;

  const unitIndex = getFileLocaleUnitIndex(locale, unit);

  return value * Math.pow(UNIT_DEFAULT_KILOBYTE, Number(unitIndex));
};

const convertFileLocaleUnitSize = (
  value: number,
  locale: keyof typeof UNIT_LOCALE = 'EN',
  fromUnit: (typeof UNIT_LOCALE)[typeof locale][(typeof UNIT_DEFAULT_SIZES)[number]] = 'MB',
  toUnit: (typeof UNIT_LOCALE)[typeof locale][(typeof UNIT_DEFAULT_SIZES)[number]] = 'GB',
) => {
  if (value <= 0) return 0;

  const bytes = createFileLocaleUnitSize(value, locale, fromUnit);
  const toUnitIndex = getFileLocaleUnitIndex(locale, toUnit);

  return bytes / Math.pow(UNIT_DEFAULT_KILOBYTE, Number(toUnitIndex));
};

const formatFileLocaleUnitSize = (
  size: number,
  locale: keyof typeof UNIT_LOCALE = 'FA',
  decimals: number = 1,
  replaceValue?: string,
) => {
  if (size <= 0) return `0 ${UNIT_LOCALE[locale].B}`;

  const unitIndex = Math.floor(
    Math.log(size) / Math.log(UNIT_DEFAULT_KILOBYTE),
  );
  const clampedIndex = Math.min(unitIndex, UNIT_DEFAULT_SIZES.length - 1);
  const convertedSize = fixedParseFloat(
    size / Math.pow(UNIT_DEFAULT_KILOBYTE, clampedIndex),
    decimals,
  );

  return `${combineNumber(String(convertedSize), replaceValue)} ${UNIT_LOCALE[locale][UNIT_DEFAULT_SIZES[clampedIndex]]}`;
};

export {
  adjustPageNumber,
  cn,
  combineNumber,
  convertFileLocaleUnitSize,
  createFileLocaleUnitSize,
  createRange,
  createTimestamp,
  deepFlatFilter,
  filterObjectFromEntries,
  fixedParseFloat,
  flatMapTreeOption,
  formatFileLocaleUnitSize,
  getFileLocaleUnitIndex,
  getObjectEntries,
  getObjectFromEntries,
  getObjectValues,
  getTimeProgress,
  hasIntersection,
  joinDecodedSegments,
  joinEncodedSegments,
  normalizeToString,
  padString,
  parseFromContent,
  preciseRound,
  randomId,
  replaceExtraSeparator,
  safeParseBoolean,
  throwErrorAsResponse,
  triggerPathname,
  validateMediaFile,
};
